import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

function About() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('help');
  const [openAccordion, setOpenAccordion] = useState(null);

  // Parse URL parameters to set initial state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    
    if (section) {
      // Map section parameter to accordion IDs
      // 'friends' maps onto 'favorites': the two sections were merged when the
      // /friends route went away, and nothing links to a friends section any
      // more. Kept as an alias so older bookmarked links still land somewhere
      // sensible rather than silently opening nothing.
      const sectionMap = {
        'all-games': 'all-games',
        'favorites': 'favorites',
        'friends': 'favorites',
        'score-entry': 'score-entry',
        'contact': 'contact'
      };
      
      if (sectionMap[section]) {
        setActiveTab('help');
        setOpenAccordion(sectionMap[section]);
      }
    }
  }, [location.search]);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const helpContent = [
    {
      id: 'all-games',
      title: 'Browse Games',
      content: <>This is where you can discover games you like. Search or filter by category and click on games to try them out. After you've visited a game, it will be dimmed. If you like a game, click the heart to add it to your My Games list on the home page for quick access. Your favorites are saved as soon as you add them — sign in to keep them if you switch devices or clear your browser. Some games have icons beside the name. <img src="/img/new.png" alt="new" className="inline h-[1em] align-text-bottom" /> means the game is new. <img src="/img/lock.png" alt="lock" className="inline h-[1em] align-text-bottom" /> means the game requires an account to play. And <img src="/img/plug.png" alt="plug" className="inline h-[1em] align-text-bottom" /> means the game is linked up with this site to automatically log your score for you.</>
    },
    {
      // The old separate "Friends" section is folded in here. It went
      // unreachable when the /friends route was deleted — the Navbar's help
      // link has no branch for it — and its copy still described adding
      // friends by username, which was retired with D39.
      id: 'favorites',
      title: 'My Games & Scores',
      content: `Games you like appear in My Games at the top of the home page. Next to each game is a button to enter or update your score for today — no account needed. Click "Manage list" at the bottom to reorder or remove games, or add a custom link to another game or site you visit regularly, right from the home page. Below that, Scores compares today's results with your friends': each game played today has a column, with each player's score in the rows below it, and the next and back buttons to the left of the game icons show more games. A speech bubble means someone left a comment with their score — click the score to read it. The Stats link under the table charts your scores over time. Connecting with friends happens through a shared link, which is on the way.`
    },
    {
      id: 'score-entry',
      title: 'Score Entry',
      content: `After playing a game and return to this site, you'll be prompted with a form to easily enter your score and, if you like, add a short comment. If you failed to complete the puzzle, just click the X button to log that. You can also add or edit a score by clicking the button next to a game in My Games.`
    },
    {
      id: 'contact',
      title: 'Contact Me',
      content: `Found a bug? Have a suggestion? Want to recommend a game for the collection? Would you like to collaborate on something? I'd love to hear from you! Please reach out at dave@nicelight.games with any feedback, issues, or ideas. Your input helps make NiceLight.Games better for everyone.`
    }
  ];

  const aboutContent = [
    {
      id: 'mission',
      title: 'The Mission',
      content: `NiceLight.Games is designed to provide moments of wholesome enjoyment and mental stimulation - a refreshing alternative to addictive social media and endless doomscrolling. In a world where so much of our screen time leaves us feeling drained, we believe in the power of purposeful play. These daily puzzle games offer a satisfying challenge, a sense of accomplishment, and a reason to return each day that actually enriches your life rather than depleting it.`
    },
    {
      id: 'pledge',
      title: 'The Pledge',
      content: `To keep this site beautiful, efficient, and aligned with your best interests, I pledge to avoid showing advertising if at all possible while still covering the costs of keeping the site running. Also, I promise I will never personally earn more from this site than I've earned as a developer (adjusted for inflation). The success of this site depends on users enjoying and sharing it, and I will not betray you by establishing a network effect and then exploiting you to maximize profit. This site exists to serve you, not to monetize your attention. If the community finds value in NiceLight.Games, I trust that support will come in forms that don't compromise its focused, humane experience.`
    },
    {
      id: 'about-me',
      title: 'About Me',
      content: `Hi! I'm Dave Berzack - a web developer, game designer, musician, and generally artsy fellow who wants to make the internet a better place. I believe the web should be a space for creativity, connection, and joy rather than manipulation and distraction. I love collaborating with like-minded people who share this vision, so please don't hesitate to reach out at dave@nicelight.games. Let's build something beautiful together!`
    }
  ];

  const currentContent = activeTab === 'help' ? helpContent : aboutContent;

  return (
    <div className="min-h-screen bg-nlg-bg flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Tab Navigation */}
        <div className="flex gap-2 px-1">
          <button
            onClick={() => setActiveTab('help')}
            className={`flex-1 py-3 px-6 font-display font-bold text-base transition-colors duration-150 rounded-t-xl ${
              activeTab === 'help'
                ? 'bg-nlg-block text-nlg-cream'
                : 'bg-white/[0.03] text-nlg-muted'
            }`}
          >
            Help
          </button>
          <button
            onClick={() => setActiveTab('about')}
            className={`flex-1 py-3 px-6 font-display font-bold text-base transition-colors duration-150 rounded-t-xl ${
              activeTab === 'about'
                ? 'bg-nlg-block text-nlg-cream'
                : 'bg-white/[0.03] text-nlg-muted'
            }`}
          >
            About
          </button>
        </div>

        {/* Accordion Content */}
        <div className="bg-nlg-block border border-nlg-rule rounded-2xl rounded-tl-none p-5 sm:p-7">
          <h1 className="font-display font-black text-2xl sm:text-3xl text-nlg-cream mb-5">
            {activeTab === 'help' ? 'How to Use NiceLight.Games' : 'About NiceLight.Games'}
          </h1>

          <div className="space-y-2">
            {currentContent.map((item) => (
              <div key={item.id} className="border border-nlg-rule rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleAccordion(item.id)}
                  className="w-full px-4 py-3 bg-white/[0.03] transition-colors duration-150 flex items-center justify-between text-left"
                >
                  <span className="font-display font-bold text-nlg-cream text-base">{item.title}</span>
                  <svg
                    className={`w-5 h-5 text-nlg-muted transition-transform duration-300 flex-shrink-0 ml-2 ${
                      openAccordion === item.id ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openAccordion === item.id
                      ? 'max-h-96 opacity-100'
                      : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-4 py-4 text-sm text-nlg-muted leading-relaxed">
                    {item.content}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Back to Games button */}
          <div className="mt-7 flex justify-center">
            <Link
              to="/"
              className="px-6 py-3 bg-nlg-gold text-nlg-bg font-bold rounded-full transition-transform duration-150 hover:scale-[1.03]"
            >
              Back to Games
            </Link>
          </div>

          <div className="mt-6 text-center text-sm text-nlg-muted">
            v1.02 © 2026 Dave Berzack
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;